import { supabase } from "./supabase";

export type LandingHeroImage = {
  id: string;
  imageUrl: string;
  storagePath: string;
  position: number;
};

type LandingHeroImageRow = {
  id: string;
  image_url: string;
  storage_path: string;
  position: number;
};

const mapHeroImage = (row: LandingHeroImageRow): LandingHeroImage => ({
  id: row.id,
  imageUrl: row.image_url,
  storagePath: row.storage_path,
  position: row.position,
});

export async function readLandingHeroImages(): Promise<LandingHeroImage[]> {
  const { data, error } = await supabase
    .from("landing_hero_images")
    .select("id,image_url,storage_path,position")
    .order("position", { ascending: true });
  if (error) throw error;
  return (data as LandingHeroImageRow[]).map(mapHeroImage);
}

export async function uploadLandingHeroImage(file: File, position: number): Promise<LandingHeroImage> {
  if (!file.type.startsWith("image/")) throw new Error("Choose an image file.");
  if (file.size > 8 * 1024 * 1024) throw new Error("Each image must be 8 MB or smaller.");

  const extension = file.name.split(".").pop()?.toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const storagePath = `${crypto.randomUUID()}.${extension}`;
  const { error: uploadError } = await supabase.storage.from("landing-hero").upload(storagePath, file, {
    cacheControl: "31536000",
    contentType: file.type,
  });
  if (uploadError) throw uploadError;

  const { data: publicUrl } = supabase.storage.from("landing-hero").getPublicUrl(storagePath);
  const { data, error } = await supabase
    .from("landing_hero_images")
    .insert({ image_url: publicUrl.publicUrl, storage_path: storagePath, position })
    .select("id,image_url,storage_path,position")
    .single();
  if (error) {
    await supabase.storage.from("landing-hero").remove([storagePath]);
    throw error;
  }
  return mapHeroImage(data as LandingHeroImageRow);
}

export async function removeLandingHeroImage(image: LandingHeroImage): Promise<void> {
  const { error } = await supabase.from("landing_hero_images").delete().eq("id", image.id);
  if (error) throw error;
  const { error: storageError } = await supabase.storage.from("landing-hero").remove([image.storagePath]);
  if (storageError) throw storageError;
}
